import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Bot, BarChart3, Users } from 'lucide-react';
import { aiProposalsAPI } from '../lib/api';

export function AIUsageDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    aiProposalsAPI.dashboard().then(res => setData(res.data)).catch(() => {});
  }, []);

  if (!data || !data.enabled) return null;

  const pct = data.units_included > 0 ? (data.units_used / data.units_included) * 100 : 0;
  const isWarning = pct >= 80;
  const isCritical = pct >= 95;

  return (
    <Card className="border-emerald-200/60" data-testid="ai-usage-dashboard">
      <CardContent className="p-3 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Bot className="w-3.5 h-3.5 text-emerald-700" />
            </div>
            <span className="text-sm font-semibold">StockBot</span>
          </div>
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full border border-emerald-200 text-emerald-700 bg-emerald-50">
            {data.tier?.toUpperCase()}
          </span>
        </div>

        {/* Usage bar */}
        <div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                isCritical ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-emerald-500'
              }`}
              style={{ width: `${Math.min(pct, 100)}%` }}
            />
          </div>
          <div className="flex justify-between mt-1">
            <span className={`text-xs font-semibold tabular-nums ${isCritical ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-emerald-700'}`}>
              {data.units_remaining} left
            </span>
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {data.units_used} / {data.units_included} used
            </span>
          </div>
        </div>

        {/* Breakdown — compact */}
        {data.breakdown?.length > 0 && (
          <div className="pt-1 border-t border-border/50">
            <p className="text-[10px] font-medium text-muted-foreground mb-1.5 flex items-center gap-1 uppercase tracking-wide">
              <BarChart3 className="w-3 h-3" /> This Cycle
            </p>
            <div className="space-y-0.5">
              {data.breakdown.map((b, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="capitalize text-muted-foreground">{b.action_type === 'query' ? 'Questions' : b.action_type === 'action_proposal' ? 'Proposals' : b.action_type.replace(/_/g, ' ')}</span>
                  <span className="tabular-nums font-medium">{b.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top users — only show if more than 1 */}
        {data.top_users?.length > 1 && (
          <div className="pt-1 border-t border-border/50">
            <p className="text-[10px] font-medium text-muted-foreground mb-1.5 flex items-center gap-1 uppercase tracking-wide">
              <Users className="w-3 h-3" /> Top Users
            </p>
            <div className="space-y-0.5">
              {data.top_users.slice(0, 3).map((u, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-muted-foreground truncate">{u.name}</span>
                  <span className="tabular-nums font-medium">{u.units} credits</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AIUsageDashboard;
